import {
    ExInput,
    ExMenuItem,
    ExSelect,
    ExToggle,
    MenuItemVariant,
    SelectType,
} from '@boomi/exosphere';
import { FABRIC_BASE_URL } from '../config';
import type { Connector, Option } from '../connectors/connectors';
import type { IdentityProvider } from '../identity-providers/identityProviders';
import CopyField from '../shared/CopyField';
import formStyles from '../shared/Form.module.css';
import Scopes from '../shared/Scopes';
import styles from './ConnectionFormFields.module.css';
import ConnectorTools from './ConnectorTools';

export interface FormValues {
    name: string;
    identityProviderId: string;
    clientId: string;
    enabled: boolean;
    userOptions: Record<string, string>;
    disabledTools: string[];
}

export const EMPTY_FORM_VALUES: FormValues = {
    name: '',
    identityProviderId: '',
    clientId: '',
    enabled: true,
    userOptions: {},
    disabledTools: [],
};

export const isFormComplete = (
    values: FormValues,
    connector: Connector,
    mode: { useOwnCredentials?: boolean; isEdit?: boolean } = {},
): boolean => {
    if (values.name.trim() === '') return false;
    if (!values.identityProviderId) return false;

    const isFilled = (id: string) => (values.userOptions[id] ?? '').trim() !== '';

    const userAuthRequired = connector.userAuthOptions.filter(
        (option) => option.isVisible && option.isRequired,
    );
    if (!userAuthRequired.every((option) => isFilled(option.id))) return false;

    // No appAuth when editing so we can stop here.
    if (mode.isEdit) return true;

    const appAuthVisible = connector.appAuthOptions.filter((option) => option.isVisible);
    if (connector.boomiSuppliesAppAuthOptions) {
        if (mode.useOwnCredentials && !appAuthVisible.every((option) => isFilled(option.id)))
            return false;
    } else if (
        !appAuthVisible.filter((option) => option.isRequired).every((option) => isFilled(option.id))
    ) {
        return false;
    }

    return true;
};

interface ConnectionFormFieldsProps {
    values: FormValues;
    identityProviders: IdentityProvider[];
    isLoadingIdentityProviders?: boolean;
    onChange: (patch: Partial<FormValues>) => void;
    isEdit?: boolean;
    userAuthOptions?: Option[] | undefined;
    appAuthOptions?: Option[] | undefined;
    connectorId?: string | undefined;
    boomiSuppliesAppAuthOptions?: boolean;
    scopes: string[];
    useOwnCredentials?: boolean;
    onUseOwnCredentialsChange?: (value: boolean) => void;
    connectorName: string;
    hasSubmitted: boolean;
    onToolCountChange: (count: number) => void;
}

const ConnectionFormFields = ({
    values,
    identityProviders,
    isLoadingIdentityProviders = false,
    onChange,
    isEdit = false,
    userAuthOptions = [],
    appAuthOptions = [],
    boomiSuppliesAppAuthOptions,
    scopes,
    useOwnCredentials = false,
    onUseOwnCredentialsChange,
    connectorId,
    connectorName,
    hasSubmitted,
    onToolCountChange,
}: ConnectionFormFieldsProps) => {
    const noIdentityProviders = !isLoadingIdentityProviders && identityProviders.length === 0;

    const renderOption = (option: Option, required: boolean) => {
        const value = values.userOptions[option.id] ?? '';
        const invalid = hasSubmitted && required && value.trim() === '';
        return (
            <div key={option.id} className={formStyles.field}>
                {option.options ? (
                    <ExSelect
                        label={option.label}
                        required={required}
                        invalid={invalid}
                        helpText={option.help ?? ''}
                        valueBasedSelection
                        showSelectedLabel
                        type={SelectType.SINGLE}
                        selected={value}
                        onChange={(e) =>
                            onChange({
                                userOptions: {
                                    ...values.userOptions,
                                    [option.id]: (e as CustomEvent<{ value: string }>).detail.value,
                                },
                            })
                        }
                    >
                        {Object.entries(option.options).map(([label, optionValue]) => (
                            <ExMenuItem
                                key={optionValue}
                                value={optionValue}
                                variant={MenuItemVariant.OPTION}
                            >
                                {label}
                            </ExMenuItem>
                        ))}
                    </ExSelect>
                ) : (
                    <ExInput
                        label={option.label}
                        required={required}
                        invalid={invalid}
                        helpText={option.help ?? ''}
                        type={option.type === 'password' ? 'password' : 'text'}
                        {...(option.type === 'password' ? { togglePassword: true } : {})}
                        value={value}
                        onInput={(e) =>
                            onChange({
                                userOptions: {
                                    ...values.userOptions,
                                    [option.id]: (e.target as HTMLInputElement).value,
                                },
                            })
                        }
                        {...(option.exampleValue ? { placeholder: option.exampleValue } : {})}
                    />
                )}
            </div>
        );
    };

    const idpPlaceholder = isLoadingIdentityProviders
        ? 'Loading…'
        : noIdentityProviders
          ? 'No identity providers available'
          : 'Select an identity provider…';

    return (
        <div className={formStyles.fields}>
            <ExInput
                data-pendo-input="home-createconfig-name"
                label="Config Name"
                type="text"
                value={values.name}
                onInput={(e) => onChange({ name: (e.target as HTMLInputElement).value })}
                placeholder={`e.g. My ${connectorName} Config`}
                invalid={hasSubmitted && values.name.trim() === ''}
                required
            />

            <div className={formStyles.field}>
                <ExSelect
                    data-pendo-select="home-createconfig-idp"
                    label="Select Identity Provider"
                    valueBasedSelection
                    showSelectedLabel
                    required
                    invalid={hasSubmitted && !values.identityProviderId}
                    placeholder={idpPlaceholder}
                    type={SelectType.SINGLE}
                    selected={values.identityProviderId}
                    disabled={isLoadingIdentityProviders || noIdentityProviders}
                    onChange={(e) =>
                        onChange({
                            identityProviderId: (e as CustomEvent<{ value: string }>).detail.value,
                        })
                    }
                >
                    {identityProviders.map((idp) => (
                        <ExMenuItem key={idp.id} value={idp.id} variant={MenuItemVariant.OPTION}>
                            {idp.name}
                        </ExMenuItem>
                    ))}
                </ExSelect>
            </div>

            {isEdit && (connectorId || values.clientId) && (
                <div className={formStyles.credentials}>
                    {connectorId && (
                        <CopyField
                            data-pendo-copyfield="editconfig-form-copymcpurl"
                            label="MCP URL"
                            value={`${FABRIC_BASE_URL}/connector/${connectorId}/mcp`}
                        />
                    )}
                    {values.clientId && (
                        <CopyField
                            data-pendo-copyfield="editconfig-form-copyclientid"
                            label="Client ID"
                            value={values.clientId}
                        />
                    )}
                </div>
            )}

            {userAuthOptions
                .filter((o) => o.isVisible)
                .map((option) => renderOption(option, option.isRequired))}

            {!isEdit &&
                appAuthOptions.length > 0 &&
                (boomiSuppliesAppAuthOptions ? (
                    <div className={styles.authManagementSection}>
                        <span className={styles.authManagementLabel}>
                            Authentication Management
                        </span>
                        <div className={styles.authManagementCard}>
                            <div className={styles.authManagementHeader}>
                                <div className={styles.authManagementInfo}>
                                    <span className={styles.authManagementTitle}>
                                        Application authentication options
                                    </span>
                                    <span className={styles.authManagementSubtitle}>
                                        Manage authentication with custom credentials
                                    </span>
                                </div>
                                <ExToggle
                                    data-pendo-toggle="home-createconfig-useowncredentials"
                                    on={useOwnCredentials}
                                    onChange={(e) =>
                                        onUseOwnCredentialsChange?.(
                                            (e as unknown as CustomEvent<boolean>).detail,
                                        )
                                    }
                                />
                            </div>
                            {useOwnCredentials &&
                                appAuthOptions
                                    .filter((o) => o.isVisible)
                                    .map((option) => renderOption(option, true))}
                            {useOwnCredentials && <Scopes scopes={scopes} />}
                        </div>
                    </div>
                ) : (
                    <>
                        {appAuthOptions
                            .filter((o) => o.isVisible)
                            .map((option) => renderOption(option, option.isRequired))}
                        <Scopes scopes={scopes} />
                    </>
                ))}

            {connectorId && (
                <ConnectorTools
                    connectorId={connectorId}
                    disabledTools={values.disabledTools}
                    onDisabledToolsChange={(disabledTools) => onChange({ disabledTools })}
                    onToolCountChange={onToolCountChange}
                />
            )}
        </div>
    );
};

export default ConnectionFormFields;
